import { useEffect, useRef, useState } from "preact/hooks"
import type { Store, StoreValue } from "nanostores"

// @nanostores/preact 0.3 starts listening from an effect that Preact runs only after the
// next animation frame, and it never re-reads the store once subscribed. A change that
// lands between the first render and that effect (e.g. a fast /sign_up/support response)
// was never rendered, leaving the popup on its loading screen. Re-check once subscribed.
export function useStore<S extends Store>(store: S): StoreValue<S> {
  const [, forceRender] = useState({})
  const value = store.get()
  const rendered = useRef(value)
  rendered.current = value

  useEffect(() => {
    let timer: number | undefined
    const rerender = () => {
      if (timer != null) return
      timer = window.setTimeout(() => {
        timer = undefined
        forceRender({})
      })
    }
    const unbind = store.listen(rerender)
    if (store.get() !== rendered.current) rerender()
    return () => {
      unbind()
      if (timer != null) clearTimeout(timer)
    }
  }, [store])

  return value
}
